import { push } from 'connected-react-router';
import { compose, composeU } from '@bem-react/core';
import React, { FC, useState, useEffect, useCallback, ReactElement, useMemo, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { Language } from '@yandex-int/i18n';

import {
    selectStreamDetails,
    requestStreamDetails,
    flushStreamDetails,
    flushStreamError,
    streamEndEvent,
} from 'ducks/stream';
import {
    hideYandexLamp,
    showYandexLamp,
} from 'ducks/yandexLamp';
import { setParamsYandexBug } from 'ducks/yandexBug';
import { selectCurrentUser } from 'ducks/user';
import {
    selectPlayerType,
    setPlayerType,
} from 'ducks/streamPlayer';

import {
    usePlatformContext,
    useKhTitle,
} from 'hooks';

import { ViewersSocket } from 'components/ViewersSocket';

import { useStreamLanguage } from 'hooks/useStreamLanguage';

import { useLeaveHandlers } from 'hooks';
import { withTypeEther } from './_type/KhStreamScreen_type_ether';
import { withLoading } from './_loading/KhStreamScreen_loading';
import { withEmpty } from './_empty/KhStreamScreen_empty';
import { withError } from './_error/KhStreamScreen_error';
import { KhStreamScreenContainerMatchParams } from './KhStreamScreen.interface';
import { i18n } from './KhStreamScreen.translations';
import { KhStreamScreenMobile as KhStreamScreenMobileBase } from './KhStreamScreen@mobile';
import { KhStreamScreenDesktop as KhStreamScreenDesktopBase } from './KhStreamScreen@desktop';

const KhStreamScreenMobile = compose(
    withEmpty,
    withError,
    withLoading,
    composeU(
        withTypeEther,
    ),
)(KhStreamScreenMobileBase);

const KhStreamScreenDesktop = compose(
    withEmpty,
    withError,
    withLoading,
    composeU(
        withTypeEther,
    ),
)(KhStreamScreenDesktopBase);

export const KhStreamScreen: FC = (): ReactElement => {
    const dispatch = useDispatch();
    const playerType = useSelector(selectPlayerType);
    const { environment } = useSelector(selectCurrentUser);
    const {
        responseError,
        name,
        streamDescription,
        streamId,
        chatType,
        chatInviteHash,
        isRequesting,
        retranslatorHost,
        etherUrl,
        etherBackId,
        eventId,
        id,
        uri,
        stopTime,
        event,
        translatedStreams,
        lang,
        viewersCount: initialViewersCount,
    } = useSelector(selectStreamDetails);

    const match = useRouteMatch<KhStreamScreenContainerMatchParams>();

    const platform = usePlatformContext();
    const KhStreamScreenView = platform === 'mobile' ? KhStreamScreenMobile : KhStreamScreenDesktop;

    const callId = match.params.id;

    const [viewersCount, setViewersCount] = useState<number>(null);

    const [isFirstMount, setIsFirstMount] = useState<boolean>(true);

    const [streamLangSetting, setStreamLangSetting] = useStreamLanguage();

    useEffect((): FunctionVoid => {
        if (responseError) {
            return;
        }

        if (!etherBackId) {
            return;
        }

        // Проверка языка трансляции
        // Бэкенд присылает все доступные языки трансляции и id звонков для каждого языка
        // Проверем, что установленный пользовтелем язык трансляции соответствует языку текущей трансляции
        // Если нет, то редиректим трансляцию с установленным языком, если такая существует
        if (translatedStreams && streamLangSetting !== lang && translatedStreams[streamLangSetting]) {
            const streamId = translatedStreams[streamLangSetting];

            dispatch(flushStreamDetails());
            dispatch(flushStreamError());
            dispatch(push(`/streams/${streamId}`));

            return;
        }
    }, [dispatch, etherBackId, responseError, lang, streamLangSetting, translatedStreams]);

    const navigateToChat = useCallback((): void => {
        dispatch(push(`/streams/${id}/chat`));
    }, [dispatch, id]);

    const handlePlayerTypeChange = useCallback((e): void => {
        dispatch(setPlayerType(e.target.value));
    }, [dispatch]);

    const title = useMemo(() => {
        const parts = [i18n('stream-title')];

        if (name) {
            parts.unshift(`${name}`);
        }

        return parts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, responseError]);

    useKhTitle(title);

    useEffect((): void => {
        setIsFirstMount(false);
    }, []);

    useEffect((): FunctionVoid => {
        if (platform === 'mobile') {
            dispatch(hideYandexLamp());
        }

        return () => {
            if (platform === 'mobile') {
                dispatch(showYandexLamp());
            }
        };
    }, [platform, dispatch]);

    useEffect((): FunctionVoid => {
        if (!id) {
            dispatch(requestStreamDetails(callId));
        }

        return (): void => {
            dispatch(flushStreamDetails());
            dispatch(flushStreamError());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callId, dispatch, match.path, streamLangSetting]);

    useEffect(() => {
        setViewersCount(initialViewersCount);
    }, [initialViewersCount]);

    useEffect(() => {
        const REPORT_TOPIC = 'answer_choices_153124';
        const PLAYER_ISSUE = '555909';

        dispatch(setParamsYandexBug({ [REPORT_TOPIC]: PLAYER_ISSUE }));

        return () => {
            dispatch(setParamsYandexBug({}));
        };
    }, [dispatch]);

    useLeaveHandlers({
        onTabLeave: () => dispatch(streamEndEvent({ id, endEvent: 'tabLeave' })),
        onTabReturn: () => dispatch(streamEndEvent({ id, endEvent: 'tabReturn' })),
        onPageLeave: () => dispatch(streamEndEvent({ id, endEvent: 'pageLeave' })),
        onTabClose: () => dispatch(streamEndEvent({ id, endEvent: 'tabClose' })),
    });

    const handleReceiveViewersNumber = useCallback((viewersCount: number) => {
        setViewersCount(viewersCount);
    }, []);

    const handleStreamLanguageChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as Language;

        if (value !== streamLangSetting) {
            setStreamLangSetting(value);

            const streamId = translatedStreams[value];

            dispatch(flushStreamDetails());
            dispatch(flushStreamError());
            dispatch(push(`/streams/${streamId}`));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translatedStreams, streamLangSetting]);

    // Определяем дату окончания трансляции
    // Если пришли данные о календарной встрече, то дата окончания трансляции равна дате окончания календарной встречи
    // Иначе дата окончания трансляции равна дате окончания звонка
    const streamStopTime = useMemo(() => event ? event.endTime : stopTime, [event, stopTime]);

    return (
        <>
            <ViewersSocket
                onReceiveViewersNumber={handleReceiveViewersNumber}
            />
            <KhStreamScreenView
                loading={isFirstMount || isRequesting}
                error={Boolean(responseError)}
                responseError={responseError}
                empty={!streamId}
                name={name}
                etherUrl={etherUrl}
                description={streamDescription}
                streamId={streamId}
                chatType={chatType}
                chatInviteHash={chatInviteHash}
                env={environment}
                eventId={eventId}
                retranslatorHost={retranslatorHost}
                navigateToChat={navigateToChat}
                type="ether"
                playerType={playerType}
                onPlayerTypeChange={handlePlayerTypeChange}
                viewersCount={viewersCount}
                uri={uri}
                stopTime={streamStopTime}
                translatedStreams={translatedStreams}
                lang={lang}
                onStreamLanguageChanged={handleStreamLanguageChanged}
            />
        </>
    );
};
